import { elevation, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontWeights } from "@dana/MobiusOverrides/styles/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    orgId = "orgId",
    chatUrl = "chatUrl",
    scrt2URL = "scrt2URL",
    developerName = "developerName",
    isEnabled = "isEnabled"
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
    session: state.context.session,
    domains: state.context.website.uri
});

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
    fields: {
        [fields.orgId]: string;
        [fields.chatUrl]: string;
        [fields.scrt2URL]: string;
        [fields.developerName]: string;
        [fields.isEnabled]: string;
    };
}
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    mainFooterGridItem?: GridItemProps;
    footerGridWrapper?: GridContainerProps;
    footerGridItemCopyrts?: GridItemProps;
    footerLinkList?: GridItemProps;
    subFooterContainer?: GridContainerProps;
    subFooterMainContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    footerLinksWrapper?: InjectableCss;
    websiteLogoGridItem: GridItemProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    mainFooterGridItem: {
        width: 12,
    },
    footerGridWrapper: {
        isFullWidth: true,
        gap: 0,

        offsetCss: css`
            max-width: 1440px;
            margin: auto;
        `,

        css: css`
            padding: 40px;
            background: ${themeColors.primary.black};
            color: ${themeColors.primary.white};
            > div {
                gap: 32px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 32px 16px;
                            
                            >div {
                                gap: 24px;
                            }
                        }
                    `,
                    ],
                    "max",
                )}
               
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            > [class*="GridOffset"] {
                                flex-wrap: nowrap;
                                > [class*="GridItemStyle"]:last-child {
                                    margin-left: auto;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    footerLinkList: {
        width: [12, 12, 8, 8, 8],
        align: "middle",
        css: css`
            gap: 32px;
            justify-content: end;
            min-height: 26px;
            > div {
                display: inline-flex;
                align-items: center;
                width: auto;
  				&:last-child {
                    flex-shrink: 0;
                }        
		    }

            .list-wrapper {
                text-transform: none;
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                padding: 0;
                gap: 8px 32px;

                .links-wrapper {
                    padding: 0;
                }

                a {
                    span {
                        color: ${themeColors.secondary.danaBrightBlue};
                        font-weight: ${fontWeights.medium};

                        &:hover {
                            &,
                            svg {
                                color: ${themeColors.primary.white};
                            }
                        }
                    }

                    [class*="IconWrapper"] {
                        color: inherit;
                    }

                    &:focus,
                    &:focus-visible {
                        color: ${themeColors.primary.white};
                        border: 1px solid ${themeColors.primary.white};
                        box-shadow: ${elevation.focus};
                        outline-width: 0;
                        outline-offset: 0;
                        border-radius: 0;

                        span {
                            &,
                            svg {
                                color: ${themeColors.primary.white};
                            }
                        }
                    }
                }
            }
            
            .title-wrapper {
                display: none;
            }
            .social-links {
                padding: 0;
                .link-wrapper {
                    display: flex;
                    align-items: center;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            justify-content: flex-start;
                            flex-direction: column;
                            align-items: flex-start;
                            gap: 24px;
                            .list-wrapper {
                                gap: 0 32px;
                            }
                        `,
                    ],
                    "max",
                )}
                 @media only screen and (min-width: 992px) and (max-width: 1199px) {
                    max-width: 64%;
                    flex-basis: 64%;
                }
                @media screen and (max-width: 767px) {
                    .list-wrapper {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px 0;
                        .links-wrapper {
                            padding: 8px 0;;
                        }
                    }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            max-width: initial;
                            flex-basis: initial;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    footerGridItemCopyrts: {
        width: [12, 12, 4, 4, 4],
        align: "middle",
        css: css`
            @media only screen and (min-width: 992px) and (max-width: 1199px) {
                max-width: 36%;
                flex-basis: 36%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            order: 3;
                        `,
                    ],
                    "max",
                )}
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            max-width: initial;
                            flex-basis: initial;
                        `,
                    ],
                    "min",
                )}
        `,
    },

    websiteLogoGridItem: {
        css: css`
            [class*="HiddenStyle-"] {
                display: block;
            }
        `,
    },
};

const FooterContainer = ({ id, extendedStyles, session, domains, fields }: Props) => {

    useEffect(() => {
        if (fields.isEnabled !== "true" || !session.isAuthenticated || (session.properties.userType ?? "") !== "Customer") {
            return;
        }
        try {
            const isUat = window.location.host.includes('localhost:3000') || window.location.host.includes('insitesandbox.com');
            // @ts-ignore
            embeddedservice_bootstrap.settings.language = session.language?.languageCode;

            // @ts-ignore
            embeddedservice_bootstrap.init(
                fields.orgId,
                fields.developerName,
                fields.chatUrl,
                {
                    scrt2URL: fields.scrt2URL
                }
            );
        } catch (err) {
            console.error('Error loading Embedded Messaging: ', err);
        }
    }, []);

    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    return (
        <>
            <GridContainer {...styles.widgetGridContainer} gap={0} isFullWidth={true}>
                <GridItem className="main-footer-container" {...styles.mainFooterGridItem}>
                    <GridContainer className="footer-grid-wrapper" {...styles.footerGridWrapper}>
                        <GridItem className="footer-copy-right text-body-sm" {...styles.footerGridItemCopyrts}>
                            <Zone contentId={id} zoneName="FooterCopyright" />
                        </GridItem>
                        <GridItem className="footer-link-list text-body-sm" {...styles.footerLinkList}>
                            <Zone contentId={id} zoneName="FooterLinkList" />
                            <Zone contentId={id} zoneName="FooterSocialIcons" />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </>
    );
};

const footerContainer: WidgetModule = {
    component: connect(mapStateToProps)(FooterContainer),
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        fieldDefinitions: [
            {
                name: fields.orgId,
                editorTemplate: "TextField",
                displayName: "Org Id",
                defaultValue: "00DWA000003csdZ",
                fieldType: "General",
                isRequired: true,
                sortOrder: 0,
            },
            {
                name: fields.chatUrl,
                editorTemplate: "TextField",
                displayName: "Chat Url",
                defaultValue: "https://dana--uat.sandbox.my.site.com/ESWUATDanaChat1760355628100",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.scrt2URL,
                editorTemplate: "TextField",
                displayName: "Scrt 2 Url",
                defaultValue: "https://dana--uat.sandbox.my.salesforce-scrt.com",
                fieldType: "General",
                isRequired: true,
                sortOrder: 2,
            },
            {
                name: fields.developerName,
                editorTemplate: "TextField",
                displayName: "EmbeddedServiceConfig DeveloperName",
                defaultValue: "UAT_Dana_Chat",
                fieldType: "General",
                isRequired: true,
                sortOrder: 3,
            },
            {
                name: fields.isEnabled,
                editorTemplate: "TextField",
                displayName: "Is Enabled",
                defaultValue: "true",
                fieldType: "General",
                isRequired: true,
                sortOrder: 4,
            },
        ],
    },
};

export default footerContainer;
